import { Link } from 'react-router-dom';
import { LandingNav, LandingFooter } from './LandingShell';
import { Section, H3, Callout } from './PolicyComponents';

const EFFECTIVE_DATE = 'March 23, 2026';

export function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-surface-base-light dark:bg-surface-base-dark">
      <LandingNav />
      <main className="max-w-3xl mx-auto px-6 py-12">
        <header className="mb-10 pb-8 border-b border-border-subtle-light dark:border-border-subtle-dark">
          <h1 className="text-3xl font-bold text-text-primary-light dark:text-text-primary-dark tracking-tight mb-2">
            Terms of Service
          </h1>
          <p className="text-sm text-text-tertiary-light dark:text-text-tertiary-dark">
            Effective Date: {EFFECTIVE_DATE}
          </p>
        </header>

        <div className="prose space-y-10 text-text-primary-light dark:text-text-primary-dark text-[15px] leading-relaxed">

          {/* 1. Agreement to Terms */}
          <Section id="agreement" title="1. Agreement to Terms">
            <Callout>
              BY ACCESSING OR USING THE SERVICES, YOU AGREE TO BE BOUND BY THESE TERMS OF SERVICE
              (&ldquo;TERMS&rdquo;). IF YOU DO NOT AGREE, YOU MUST IMMEDIATELY STOP USING THE
              SERVICES. YOUR CONTINUED USE CONSTITUTES ONGOING ACCEPTANCE OF THESE TERMS AND ANY
              FUTURE MODIFICATIONS.
            </Callout>
            <p>
              These Terms constitute a legally binding agreement between you (&ldquo;User,&rdquo;
              &ldquo;you,&rdquo; or &ldquo;your&rdquo;) and The Craft Lab LLC (&ldquo;Company,&rdquo;
              &ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) governing your access to and
              use of the Danny Tasks application, website, browser extension, API, CLI, and all
              related services (collectively, the &ldquo;Services&rdquo;).
            </p>
            <p>
              We reserve the right to modify these Terms at any time by posting revised Terms on the
              Services. Changes are effective immediately upon posting. It is your sole responsibility
              to review these Terms periodically. Your continued use of the Services after any
              modification constitutes your acceptance of the revised Terms.
            </p>
          </Section>

          {/* 2. Eligibility */}
          <Section id="eligibility" title="2. Eligibility">
            <p>
              You must be at least 13 years old (or 16 in the EEA/UK) to use the Services. By using
              the Services, you represent and warrant that you meet this age requirement and have the
              legal capacity to enter into these Terms. If you are using the Services on behalf of an
              organization, you represent that you have authority to bind that organization to these
              Terms.
            </p>
          </Section>

          {/* 3. Account Registration */}
          <Section id="accounts" title="3. Account Registration and Security">
            <p>
              To access certain features, you must create an account. You agree to:
            </p>
            <ul>
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain the security of your credentials, including passwords, API keys, and OAuth tokens</li>
              <li>Immediately notify us of any unauthorized use of your account</li>
              <li>Accept full responsibility for all activity under your account, whether authorized or not</li>
            </ul>
            <Callout>
              WE ARE NOT LIABLE FOR ANY LOSS OR DAMAGE ARISING FROM YOUR FAILURE TO MAINTAIN THE
              CONFIDENTIALITY OF YOUR ACCOUNT CREDENTIALS, INCLUDING BUT NOT LIMITED TO API KEYS,
              PASSWORDS, AND CONNECTED THIRD-PARTY SERVICE TOKENS.
            </Callout>
            <p>
              We reserve the right to suspend or terminate your account at any time, for any reason or
              no reason, with or without notice. Upon termination, your right to use the Services
              ceases immediately, and we have no obligation to retain, export, or return your data
              except as required by applicable law.
            </p>
          </Section>

          {/* 4. Description of Services */}
          <Section id="services" title="4. Description of Services">
            <p>
              Danny is an AI-powered task management platform that includes:
            </p>
            <ul>
              <li>A web application for task creation, organization, and management</li>
              <li>AI-driven features including task classification, time estimation, embedding-based search, and conversational interaction</li>
              <li>Integration with third-party services (Todoist, Google Calendar, Gmail, GitHub)</li>
              <li>A browser extension for quick task capture</li>
              <li>A command-line interface (CLI)</li>
              <li>An API for programmatic access</li>
            </ul>
            <p>
              We reserve the right to modify, suspend, or discontinue any aspect of the Services at
              any time, with or without notice, and without liability to you. We do not guarantee that
              the Services will be available at all times or without interruption.
            </p>
          </Section>

          {/* 5. Subscription and Billing */}
          <Section id="billing" title="5. Subscription, Billing, and Payments">
            <H3>5.1 Plans and Pricing</H3>
            <p>
              The Services may be offered under free and paid subscription tiers. Paid features
              require an active subscription. We reserve the right to change pricing at any time.
              Price changes take effect at the start of your next billing period.
            </p>

            <H3>5.2 Free Trial</H3>
            <p>
              New accounts may receive a free trial period for AI features. The trial begins upon
              first use of an AI-powered feature and lasts for 14 days. After the trial period,
              continued access to AI features requires a paid subscription.
            </p>

            <H3>5.3 Billing</H3>
            <p>
              Paid subscriptions are billed monthly. Charges include a base fee and usage-based AI
              costs calculated from your token consumption plus a management fee. Payment is processed
              through Stripe. By subscribing, you authorize us to charge your payment method on a
              recurring basis.
            </p>

            <H3>5.4 Refunds</H3>
            <Callout>
              ALL PAYMENTS ARE NON-REFUNDABLE EXCEPT AS EXPRESSLY REQUIRED BY APPLICABLE LAW. THIS
              INCLUDES BASE FEES, AI USAGE CHARGES, AND ANY OTHER AMOUNTS BILLED TO YOUR ACCOUNT.
            </Callout>

            <H3>5.5 Overdue Payments</H3>
            <p>
              If payment fails, we may suspend or limit your access to paid features. We may also
              charge interest on overdue balances at the rate of 1.5% per month (or the maximum
              permitted by law, whichever is lower). You are responsible for all costs of collection,
              including reasonable attorneys&rsquo; fees.
            </p>
          </Section>

          {/* 6. Acceptable Use */}
          <Section id="acceptable-use" title="6. Acceptable Use">
            <p>You agree not to:</p>
            <ul>
              <li>Use the Services for any illegal purpose or in violation of any applicable law</li>
              <li>Interfere with, disrupt, or overload the Services or their underlying infrastructure</li>
              <li>Attempt to gain unauthorized access to any part of the Services, other accounts, or connected systems</li>
              <li>Use automated means (bots, scrapers, crawlers) to access the Services except through our documented API</li>
              <li>Reverse engineer, decompile, or disassemble any part of the Services</li>
              <li>Circumvent any rate limits, usage quotas, or technical restrictions</li>
              <li>Use the Services to transmit malware, spam, or malicious content</li>
              <li>Resell, sublicense, or redistribute the Services without our written consent</li>
              <li>Use the AI features to generate content that is harmful, deceptive, or violates the rights of others</li>
              <li>Attempt to extract, train, or build competing AI models using outputs from the Services</li>
            </ul>
            <p>
              Violation of this section may result in immediate termination of your account without
              notice or refund.
            </p>
          </Section>

          {/* 7. User Content */}
          <Section id="user-content" title="7. User Content">
            <H3>7.1 Ownership</H3>
            <p>
              You retain ownership of the content you submit to the Services (task descriptions,
              project names, labels, notes, and other user-generated data). However, by using the
              Services, you grant us the licenses described below.
            </p>

            <H3>7.2 License Grant</H3>
            <Callout>
              BY SUBMITTING CONTENT TO THE SERVICES, YOU GRANT THE COMPANY A WORLDWIDE, NON-EXCLUSIVE,
              ROYALTY-FREE, SUBLICENSABLE, TRANSFERABLE LICENSE TO USE, REPRODUCE, MODIFY, PROCESS,
              ADAPT, AND DISPLAY YOUR CONTENT SOLELY TO PROVIDE, MAINTAIN, AND IMPROVE THE SERVICES.
            </Callout>
            <p>
              This license includes the right to transmit your content to third-party AI providers
              for processing (classification, estimation, embedding, chat), analytics services, and
              infrastructure providers necessary to operate the Services.
            </p>

            <H3>7.3 Aggregated Data</H3>
            <p>
              We may create anonymized, aggregated, or de-identified data from your content and usage.
              Such data is owned exclusively by us and may be used for any lawful purpose, including
              improving our products, conducting research, publishing benchmarks, and sharing with
              third parties, without any obligation or compensation to you.
            </p>

            <H3>7.4 Content Responsibility</H3>
            <p>
              You are solely responsible for all content you submit. We do not monitor, endorse, or
              assume any responsibility for user content. We reserve the right to remove any content
              that violates these Terms or that we find objectionable, in our sole discretion.
            </p>
          </Section>

          {/* 8. AI Features */}
          <Section id="ai-features" title="8. AI Features and Disclaimers">
            <Callout>
              ALL AI-GENERATED CONTENT, INCLUDING BUT NOT LIMITED TO TASK CLASSIFICATIONS, TIME
              ESTIMATES, PRIORITY SCORES, PRODUCTIVITY INSIGHTS, AND CHAT RESPONSES, IS PROVIDED
              &ldquo;AS IS&rdquo; WITHOUT ANY WARRANTY OF ACCURACY, COMPLETENESS, RELIABILITY, OR
              FITNESS FOR ANY PURPOSE.
            </Callout>
            <p>
              You acknowledge and agree that:
            </p>
            <ul>
              <li>AI outputs may contain errors, inaccuracies, or hallucinations</li>
              <li>You are solely responsible for reviewing and evaluating any AI-generated content before acting on it</li>
              <li>We do not guarantee the consistency, availability, or quality of AI features</li>
              <li>AI models and providers may change at any time without notice</li>
              <li>Your task content is sent to external AI providers (Anthropic, OpenAI) for processing, subject to their respective terms and policies</li>
              <li>We have no control over how external AI providers process, cache, or retain your data once transmitted</li>
              <li>AI features may be rate-limited, degraded, or unavailable during periods of high demand or provider outages</li>
            </ul>
            <p>
              You shall not rely on AI-generated outputs as the sole basis for any significant
              decision. The Company is not liable for any consequences arising from reliance on
              AI-generated content.
            </p>
          </Section>

          {/* 9. Third-Party Integrations */}
          <Section id="third-party-integrations" title="9. Third-Party Integrations">
            <p>
              The Services integrate with third-party platforms including Todoist, Google (Calendar,
              Gmail), GitHub, Stripe, and others. By enabling these integrations, you:
            </p>
            <ul>
              <li>Authorize us to access and exchange data with these services on your behalf</li>
              <li>Acknowledge that third-party services are governed by their own terms and privacy policies</li>
              <li>Accept all risks associated with data shared with or received from third-party services</li>
              <li>Agree that we are not responsible for the availability, accuracy, or security of third-party services</li>
            </ul>
            <Callout>
              WE DISCLAIM ALL LIABILITY FOR THE ACTS OR OMISSIONS OF THIRD-PARTY SERVICE PROVIDERS,
              INCLUDING DATA BREACHES, SERVICE OUTAGES, DATA LOSS, OR CHANGES TO THEIR TERMS OR APIS
              THAT MAY AFFECT THE FUNCTIONALITY OF THE SERVICES.
            </Callout>
          </Section>

          {/* 10. Intellectual Property */}
          <Section id="intellectual-property" title="10. Intellectual Property">
            <p>
              The Services, including all software, designs, text, graphics, interfaces, code,
              algorithms, and documentation, are owned by the Company and protected by intellectual
              property laws. Nothing in these Terms grants you any right, title, or interest in the
              Services except the limited right to use them in accordance with these Terms.
            </p>
            <p>
              &ldquo;Danny,&rdquo; &ldquo;Danny Tasks,&rdquo; the Danny logo, and all related names,
              marks, and slogans are trademarks of the Company. You may not use them without our prior
              written consent.
            </p>
          </Section>

          {/* 11. Privacy */}
          <Section id="privacy" title="11. Privacy">
            <p>
              Your use of the Services is also governed by our{' '}
              <Link to="/privacy-policy" className="text-primary-500 hover:text-primary-400 underline underline-offset-2">
                Privacy Policy
              </Link>
              , which is incorporated into these Terms by reference. By using the Services, you
              consent to the collection, use, and sharing of your information as described in the
              Privacy Policy.
            </p>
          </Section>

          {/* 12. Disclaimer of Warranties */}
          <Section id="disclaimers" title="12. Disclaimer of Warranties">
            <Callout>
              THE SERVICES ARE PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo; WITHOUT
              WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE. TO THE
              MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, WE DISCLAIM ALL WARRANTIES, INCLUDING BUT
              NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
              TITLE, NON-INFRINGEMENT, AND ANY WARRANTIES ARISING FROM COURSE OF DEALING OR USAGE
              OF TRADE.
            </Callout>
            <p>Without limiting the foregoing, we do not warrant that:</p>
            <ul>
              <li>The Services will meet your requirements or expectations</li>
              <li>The Services will be uninterrupted, timely, secure, or error-free</li>
              <li>Any data or content will be accurate, reliable, or complete</li>
              <li>Any defects will be corrected</li>
              <li>The Services are free of viruses or other harmful components</li>
              <li>AI-generated outputs will be accurate, useful, or appropriate for any purpose</li>
            </ul>
            <p>
              Some jurisdictions do not allow the exclusion of implied warranties, so some of the
              above exclusions may not apply to you. In such cases, our liability is limited to the
              maximum extent permitted by applicable law.
            </p>
          </Section>

          {/* 13. Limitation of Liability */}
          <Section id="limitation-of-liability" title="13. Limitation of Liability">
            <Callout>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL THE COMPANY, ITS
              OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AFFILIATES, OR LICENSORS BE LIABLE FOR ANY
              INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES, INCLUDING
              BUT NOT LIMITED TO DAMAGES FOR LOSS OF PROFITS, GOODWILL, DATA, USE, OR OTHER
              INTANGIBLE LOSSES, REGARDLESS OF WHETHER WE WERE ADVISED OF THE POSSIBILITY OF SUCH
              DAMAGES.
            </Callout>
            <p>This includes, without limitation, damages arising from:</p>
            <ul>
              <li>Your use or inability to use the Services</li>
              <li>Unauthorized access to or alteration of your data</li>
              <li>Loss, corruption, or deletion of your content</li>
              <li>Actions or inactions of third-party service providers</li>
              <li>Reliance on AI-generated content or recommendations</li>
              <li>Interruption, suspension, or termination of the Services</li>
              <li>Errors, bugs, or inaccuracies in the Services</li>
              <li>Any matter relating to the Services</li>
            </ul>
            <Callout>
              IN NO EVENT SHALL OUR TOTAL AGGREGATE LIABILITY ARISING OUT OF OR RELATING TO THESE
              TERMS OR THE SERVICES EXCEED THE GREATER OF: (A) THE AMOUNTS YOU HAVE ACTUALLY PAID TO
              US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) FIFTY DOLLARS ($50 USD).
            </Callout>
            <p>
              The foregoing limitations apply even if any remedy fails of its essential purpose. Some
              jurisdictions do not allow the limitation of liability for certain damages, so some of
              the above limitations may not apply to you.
            </p>
          </Section>

          {/* 14. Indemnification */}
          <Section id="indemnification" title="14. Indemnification">
            <p>
              You agree to indemnify, defend, and hold harmless the Company, its officers, directors,
              employees, agents, affiliates, successors, and assigns from and against any and all
              claims, liabilities, damages, judgments, awards, losses, costs, expenses, and fees
              (including reasonable attorneys&rsquo; fees) arising out of or relating to:
            </p>
            <ul>
              <li>Your use of or access to the Services</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights, including intellectual property rights</li>
              <li>Any content you submit through the Services</li>
              <li>Your negligence or willful misconduct</li>
              <li>Your use of or reliance on AI-generated outputs</li>
              <li>Any third-party claims arising from your use of the Services</li>
            </ul>
            <p>
              We reserve the right to assume exclusive defense and control of any matter subject to
              indemnification by you, at your expense. You agree to cooperate with our defense of
              any such claim.
            </p>
          </Section>

          {/* 15. Dispute Resolution and Arbitration */}
          <Section id="dispute-resolution" title="15. Dispute Resolution and Arbitration">
            <Callout>
              PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS, INCLUDING YOUR RIGHT
              TO FILE A LAWSUIT IN COURT AND TO HAVE A JURY TRIAL.
            </Callout>

            <H3>15.1 Binding Arbitration</H3>
            <p>
              Any dispute, claim, or controversy arising out of or relating to these Terms or the
              Services, including the determination of the scope or applicability of this agreement to
              arbitrate, shall be resolved by binding individual arbitration administered by the
              American Arbitration Association (&ldquo;AAA&rdquo;) under its Consumer Arbitration
              Rules. The arbitration shall take place in the state of the Company&rsquo;s principal
              place of business.
            </p>

            <H3>15.2 Class Action Waiver</H3>
            <Callout>
              YOU AND THE COMPANY AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR
              ITS INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS,
              CONSOLIDATED, OR REPRESENTATIVE PROCEEDING. THE ARBITRATOR MAY NOT CONSOLIDATE MORE
              THAN ONE PERSON&rsquo;S CLAIMS AND MAY NOT PRESIDE OVER ANY FORM OF CLASS OR
              REPRESENTATIVE PROCEEDING.
            </Callout>

            <H3>15.3 Jury Trial Waiver</H3>
            <p>
              TO THE EXTENT PERMITTED BY APPLICABLE LAW, YOU WAIVE ANY RIGHT TO A JURY TRIAL IN ANY
              PROCEEDING ARISING OUT OF OR RELATING TO THESE TERMS.
            </p>

            <H3>15.4 Costs</H3>
            <p>
              Each party shall bear its own costs and expenses in connection with arbitration. If you
              prevail, you may be entitled to an award of attorneys&rsquo; fees and costs only to the
              extent permitted by applicable law and the AAA rules.
            </p>

            <H3>15.5 Time Limitation</H3>
            <p>
              Any claim or cause of action arising out of or relating to these Terms or the Services
              must be filed within one (1) year after such claim or cause of action arose, or it shall
              be forever barred. This limitation applies regardless of any statute of limitations to
              the contrary.
            </p>
          </Section>

          {/* 16. Termination */}
          <Section id="termination" title="16. Termination">
            <p>
              We may terminate or suspend your account and access to the Services immediately, without
              prior notice or liability, for any reason, including without limitation if you breach
              these Terms. Upon termination:
            </p>
            <ul>
              <li>Your right to use the Services ceases immediately</li>
              <li>We may delete your account, content, and data without obligation to retain or return it</li>
              <li>Any outstanding fees remain due and payable</li>
              <li>Sections 7 (User Content licenses), 12-15 (Disclaimers, Liability, Indemnification, Arbitration), and 18-19 (Governing Law, Severability) survive termination</li>
            </ul>
            <p>
              You may terminate your account at any time by contacting us. Account termination does
              not entitle you to a refund of any prepaid fees.
            </p>
          </Section>

          {/* 17. Governing Law */}
          <Section id="governing-law" title="17. Governing Law">
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the State
              of Texas, United States, without regard to its conflict of law provisions. To the extent
              that litigation is permitted under these Terms, you consent to the exclusive
              jurisdiction of the state and federal courts located in Texas.
            </p>
          </Section>

          {/* 18. Severability */}
          <Section id="severability" title="18. Severability">
            <p>
              If any provision of these Terms is held to be invalid, illegal, or unenforceable, the
              remaining provisions shall continue in full force and effect. The invalid provision
              shall be modified to the minimum extent necessary to make it valid and enforceable while
              preserving its original intent.
            </p>
          </Section>

          {/* 19. Entire Agreement */}
          <Section id="entire-agreement" title="19. Entire Agreement">
            <p>
              These Terms, together with the{' '}
              <Link to="/privacy-policy" className="text-primary-500 hover:text-primary-400 underline underline-offset-2">
                Privacy Policy
              </Link>
              , constitute the entire agreement between you and the Company regarding the Services and
              supersede all prior and contemporaneous agreements, proposals, or representations,
              written or oral, regarding the subject matter herein.
            </p>
          </Section>

          {/* 20. Contact */}
          <Section id="contact" title="20. Contact">
            <p>
              For questions about these Terms, contact:
            </p>
            <p>
              <strong>The Craft Lab LLC</strong><br />
              Email: legal@askdanny.to
            </p>
          </Section>
        </div>

        <footer className="mt-16 pt-8 border-t border-border-subtle-light dark:border-border-subtle-dark text-center">
          <Link
            to="/"
            className="text-sm text-primary-500 hover:text-primary-400 transition-colors"
          >
            Back to Home
          </Link>
        </footer>
      </main>
      <LandingFooter />
    </div>
  );
}
